import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  GateScreen,
  GriddHeader,
  GriddNode,
  fileToBase64,
} from "@/components/gridd/GriddSections";

export default function Gridd() {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const isMaster = user?.role === "master";

  const [granted, setGranted] = useState(false);
  const [gateError, setGateError] = useState("");
  const [gateLoading, setGateLoading] = useState(false);
  const [pass, setPass] = useState("");
  const [passphrase, setPassphrase] = useState("");

  const [files, setFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesig, setNewDesig] = useState("");

  useEffect(() => {
    if (user === null) return;
    if (!isAdmin) navigate("/admin");
  }, [user, isAdmin, navigate]);

  // Master users skip the gate
  const effectiveGranted = granted || (isAdmin && isMaster);

  const headers = () => (passphrase ? { "X-Gridd-Passphrase": passphrase } : {});

  useEffect(() => {
    if (!effectiveGranted) return;
    const h = passphrase ? { "X-Gridd-Passphrase": passphrase } : {};
    api.get("/gridd", { headers: h }).then((r) => setFiles(r.data));
  }, [effectiveGranted, passphrase]);

  const unlock = async (e) => {
    e.preventDefault();
    setGateLoading(true);
    setGateError("");
    try {
      await axios.post(`${API}/gridd/access`, { passphrase: pass });
      setPassphrase(pass);
      setGranted(true);
    } catch {
      setGateError("The lattice refuses this passphrase.");
    } finally {
      setGateLoading(false);
    }
  };

  const startEdit = (f) => { setDraft({ ...f }); setEditingId(f.id); };
  const cancel = () => { setDraft(null); setEditingId(null); };

  const save = async () => {
    try {
      const res = await api.put(`/gridd/${editingId}`, draft, { headers: headers() });
      setFiles((prev) => prev.map((x) => (x.id === editingId ? res.data : x)));
      cancel();
      toast.success("Node re-woven.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Sever this node from the lattice?")) return;
    try {
      await api.delete(`/gridd/${id}`, { headers: headers() });
      setFiles((prev) => prev.filter((x) => x.id !== id));
      toast.success("Node severed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    }
  };

  const create = async () => {
    if (!newTitle.trim()) { toast.error("Title required."); return; }
    try {
      const res = await api.post(
        "/gridd",
        {
          title: newTitle.trim(),
          designation: newDesig.trim() || "GR1DD//?",
          classification: "OMEGA",
          status: "REDACTED",
          date: "",
          body: "",
          images: [],
        },
        { headers: headers() }
      );
      setFiles((prev) => [...prev, res.data]);
      setNewTitle("");
      setNewDesig("");
      setCreating(false);
      toast.success("Node inscribed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed.");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast.error("Image too large (~1.2MB max).");
      return;
    }
    const b64 = await fileToBase64(file);
    setDraft((d) => ({ ...d, images: [...(d.images || []), b64] }));
  };

  const removeImage = (i) => {
    setDraft((d) => ({ ...d, images: d.images.filter((_, idx) => idx !== i) }));
  };

  if (user === null) {
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: contacting lattice :::</div>;
  }
  if (!isAdmin) return null;
  if (!effectiveGranted) {
    return (
      <GateScreen onUnlock={unlock} error={gateError} loading={gateLoading} pass={pass} setPass={setPass} />
    );
  }

  return (
    <div className="space-y-12" data-testid="gridd-page">
      <GriddHeader
        isMaster={isMaster}
        creating={creating}
        setCreating={setCreating}
        newDesig={newDesig}
        setNewDesig={setNewDesig}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        onCreate={create}
      />

      <div className="space-y-10">
        {files.length === 0 ? (
          <div className="border border-dashed border-[#333] p-16 text-center text-[#555] text-xs tracking-widest uppercase">
            ::: the lattice is empty :::
          </div>
        ) : (
          files.map((f) => (
            <GriddNode
              key={f.id}
              f={f}
              isEditing={editingId === f.id}
              draft={draft}
              setDraft={setDraft}
              onStartEdit={startEdit}
              onSave={save}
              onCancel={cancel}
              onRemove={remove}
              onUpload={onUpload}
              removeImage={removeImage}
            />
          ))
        )}
      </div>

      <div className="border-t border-[#333] pt-6 text-[0.6rem] tracking-[0.4em] uppercase text-[#555] text-center">
        end of lattice · re-seal upon departure
      </div>
    </div>
  );
}
